/**
 * TimedReplayView — wall-clock replay from spectator events
 */

import { useMemo, useState, useEffect, useRef } from 'react';
import ReplayTable from './ReplayTable';
import type { SpectatorEvent, SpectatorChatMessage } from '../hooks/useSpectatorPlayback';
import type { GameHistory, ReplayStepState } from '../game/gameHistory';
import { CardNumber } from '../types/game';
import '../styles/spectator.css';

interface TimedReplayViewProps {
  events: SpectatorEvent[];
  chatMessages: SpectatorChatMessage[];
  players: Array<{ pseudo: string; position: number }>;
  playerCount?: number;
  compact?: boolean;
  waitingMessage?: string;
  onComplete?: () => void;
}

interface VisibleState {
  hands: Map<number, CardNumber[]>;
  trickCards: Array<{ player: number; card: CardNumber }>;
  chien: CardNumber[];
  ecart: CardNumber[];
  contract?: number;
  taker?: number;
  dealer?: number;
}

function buildInitialHands(events: SpectatorEvent[], playerCount: number): Map<number, CardNumber[]> {
  const hands = new Map<number, CardNumber[]>();
  for (const ev of events) {
    if (ev.type === 'hand_private') {
      const pos = ev.payload.position as number;
      hands.set(pos, [...(ev.payload.hand as number[])]);
    }
  }
  if (hands.size === 0) {
    for (let i = 0; i < playerCount; i++) hands.set(i, []);
  }
  return hands;
}

function applyEvent(state: VisibleState, ev: SpectatorEvent, playerCount: number): VisibleState {
  const next = {
    ...state,
    hands: new Map(state.hands),
    trickCards: [...state.trickCards],
    chien: [...state.chien],
    ecart: [...state.ecart],
  };

  switch (ev.type) {
    case 'deal_start':
      next.dealer = ev.payload.dealer as number;
      next.trickCards = [];
      break;
    case 'hand_private':
    case 'hand_updated': {
      const pos = ev.payload.position as number;
      next.hands.set(pos, [...(ev.payload.hand as number[])]);
      break;
    }
    case 'bid':
      if ((ev.payload.bid as number) > 0) {
        next.taker = ev.payload.player as number;
        next.contract = ev.payload.bid as number;
      }
      break;
    case 'chien':
      next.chien = [...(ev.payload.cards as number[])];
      break;
    case 'ecart':
      next.ecart = [...(ev.payload.cards as number[])];
      break;
    case 'card_played': {
      const player = ev.payload.player as number;
      const card = ev.payload.card as CardNumber;
      next.trickCards.push({ player, card });
      const hand = next.hands.get(player);
      if (hand) {
        const idx = hand.indexOf(card);
        if (idx >= 0) hand.splice(idx, 1);
      }
      if (next.trickCards.length >= playerCount) {
        next.trickCards = [];
      }
      break;
    }
    case 'trick_end':
      next.trickCards = [];
      break;
    default:
      break;
  }

  void playerCount;
  return next;
}

export default function TimedReplayView({
  events,
  chatMessages,
  players,
  playerCount = 4,
  compact = false,
  waitingMessage,
  onComplete,
}: TimedReplayViewProps) {
  const [visibleCount, setVisibleCount] = useState(0);
  const [speed, setSpeed] = useState(1);
  const [isPlaying, setIsPlaying] = useState(true);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const completedRef = useRef(false);

  const visibleEvents = events.slice(0, visibleCount);

  const visibleState = useMemo(() => {
    let state: VisibleState = {
      hands: buildInitialHands(events, playerCount),
      trickCards: [],
      chien: [],
      ecart: [],
    };
    for (const ev of visibleEvents) {
      state = applyEvent(state, ev, playerCount);
    }
    return state;
  }, [events, visibleEvents, playerCount]);

  const visibleChat = useMemo(() => {
    if (visibleEvents.length === 0) return [];
    const lastTs = visibleEvents[visibleEvents.length - 1]?.serverTs ?? Infinity;
    return chatMessages.filter(c => c.timestamp <= lastTs);
  }, [chatMessages, visibleEvents]);

  useEffect(() => {
    setVisibleCount(0);
    completedRef.current = false;
    setIsPlaying(events.length > 0);
  }, [events]);

  useEffect(() => {
    if (!isPlaying || visibleCount >= events.length) {
      if (visibleCount >= events.length && events.length > 0 && !completedRef.current) {
        completedRef.current = true;
        onComplete?.();
      }
      return;
    }

    const prevTs = visibleCount > 0 ? events[visibleCount - 1].serverTs : events[0].serverTs;
    const nextTs = events[visibleCount].serverTs;
    const delta = Math.max(50, Math.min((nextTs - prevTs) / speed, 4000));

    timerRef.current = setTimeout(() => {
      setVisibleCount(c => c + 1);
    }, delta);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [isPlaying, visibleCount, events, speed, onComplete]);

  const replayPlayers = players.map(p => ({
    name: p.pseudo,
    pseudo: p.pseudo,
    position: p.position,
    isConnected: true,
    isBot: false,
  }));

  const minimalHistory: GameHistory = useMemo(() => ({
    players: replayPlayers,
    playerCount,
    dealer: visibleState.dealer ?? 0,
    taker: visibleState.taker ?? -1,
    contract: visibleState.contract ?? 0,
    myPosition: 0,
    initialHands: visibleState.hands,
    chien: visibleState.chien,
    ecart: visibleState.ecart,
    bids: [],
    tricks: [],
    poignees: [],
    steps: [],
  }), [replayPlayers, playerCount, visibleState]);

  const stepState: ReplayStepState = {
    hands: visibleState.hands,
    trickCards: visibleState.trickCards.map(c => ({ ...c, timestamp: 0 })),
    chien: visibleState.chien,
    ecart: visibleState.ecart,
    poignees: [],
  };

  if (waitingMessage && events.length === 0) {
    return (
      <div className={`timed-replay-view ${compact ? 'compact' : ''}`}>
        <div className="spectator-waiting">{waitingMessage}</div>
      </div>
    );
  }

  return (
    <div className={`timed-replay-view ${compact ? 'compact' : ''}`}>
      <div className="timed-replay-controls">
        <button type="button" onClick={() => setIsPlaying(p => !p)}>
          {isPlaying ? 'Pause' : 'Lecture'}
        </button>
        <label>
          Vitesse
          <select value={speed} onChange={e => setSpeed(Number(e.target.value))}>
            <option value={0.5}>0.5×</option>
            <option value={1}>1×</option>
            <option value={1.5}>1.5×</option>
            <option value={2}>2×</option>
          </select>
        </label>
        <span className="timed-replay-progress">
          {visibleCount}/{events.length}
        </span>
      </div>

      <ReplayTable
        history={minimalHistory}
        stepState={stepState}
        showChien={visibleState.chien.length > 0}
        showEcart={visibleState.ecart.length > 0}
      />

      {!compact && visibleChat.length > 0 && (
        <div className="spectator-chat-overlay">
          {visibleChat.slice(-5).map((msg, i) => (
            <div key={i} className="spectator-chat-line">
              {msg.from && <strong>{msg.from}: </strong>}
              {msg.message}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** Convert built history to GameHistory for ReplayMode compatibility */
export function historyFromSpectatorEvents(
  events: SpectatorEvent[],
  players: Array<{ pseudo: string; position: number }>,
): GameHistory | null {
  void events;
  void players;
  return null;
}
